import React from 'react'
import { RouterProvider } from 'react-router-dom'
import useAuthStore from './stores/auth.store'
import { useServiceWorkerMessages } from './hooks/use-service-worker-messages'
import { useFCMNotifications } from './hooks/use-fcm-notifications'
import { useBugHerd } from '@hapana/shared/web/hooks/useBugHerd'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import router from './router'

const loadingContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
}

// Demo public paths that bypass auth loading (must match DEMO_PUBLIC_PATHS in index.jsx)
const DEMO_PUBLIC_PATHS = ['/signup', '/verify', '/creating', '/magic-link']
const isDemoContext = () => {
  const hostname = window.location.hostname
  if (hostname.split('.')[0] === 'demo') return true
  if (
    (hostname === 'localhost' || hostname === '127.0.0.1') &&
    import.meta.env.VITE_DEMO_STAFF_TENANT_ID
  ) {
    return true
  }
  return false
}

// Check if current path is a public route that doesn't require auth loading
const isPublicRoute = () => {
  const path = window.location.pathname
  if (path.startsWith('/confirm/')) return true
  // Login page manages its own loading state — don't replace it with a full-page loader
  if (path === '/login' || path.startsWith('/login/')) return true
  // Demo pages bypass auth loading — user may be authenticated but we don't
  // want the auth store's loading state to block the router from rendering
  if (isDemoContext() && DEMO_PUBLIC_PATHS.some((p) => path === p || path.startsWith(p + '/'))) {
    return true
  }
  // /magic-link and /reset-password are always public — Firebase redirects here without brand
  // context for both flows. The callback/page components handle their own tenant redirect.
  if (path === '/magic-link' || path.startsWith('/magic-link/')) return true
  if (path === '/reset-password' || path.startsWith('/reset-password/')) return true
  return false
}

/**
 * Main App Component
 * Auth is initialized in index.js via useAuthInitialize hook
 * Tenant loading is now handled at the root level in index.js
 */
const App = () => {
  const { loading } = useAuthStore()

  useServiceWorkerMessages()
  useFCMNotifications()
  useBugHerd()

  // Show loading only while Firebase auth is initializing
  // Skip for public routes that don't need auth
  if (loading && !isPublicRoute()) {
    return (
      <div style={loadingContainerStyle}>
        <Loading />
      </div>
    )
  }

  return <RouterProvider router={router} />
}

export default App
